import { Injectable } from '@angular/core';
import { tz } from 'moment-timezone';

import type { CopilotContextPayload } from './copilot-context.service';
import { StreamingChatService, BaseStreamCallbacks } from './streaming-chat.service';

// ============================================================================
// Copilot Domain Types
// ============================================================================

export interface CopilotRequest {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  pageContext: CopilotContextPayload | null;
  /**
   * Full set of pageIds currently visible to the staff member. Usually one,
   * but two on sidebar URLs like `/inbox/me/view/<id>` where both the inbox
   * list and the conversation detail are rendered simultaneously. The BE
   * uses this for conditional tool registration so e.g.
   * `update_conversation_filters` is available when the inbox is visible —
   * even when `pageContext.pageId` is `conversation-details`.
   */
  activePageIds?: string[];
  /**
   * Full context payloads for every currently-visible page. The system
   * prompt still uses `pageContext` (the primary, deepest tag) — but
   * BE tools that need a *non-primary* page's context (e.g.
   * `update_conversation_filters` on a sidebar URL where inbox is visible
   * but conversation-details is primary) look this up here. Backward-
   * compatible: if missing, BE falls back to `[pageContext]`.
   */
  activeContexts?: CopilotContextPayload[];
  sessionId: string;
  currentUrl?: string;
  /** The browser's timezone, so the copilot states today's date in it. Filled in by the service. */
  clientTimezone?: string;
}

export interface CopilotResponse {
  sessionId: string;
  message: string;
}

export interface CopilotResumeRequest {
  sessionId: string;
  runId: string;
  type: 'tool_approval_result' | 'oauth_result';
  approved: boolean;
  approvalId?: string;
  modifiedArgs?: Record<string, unknown>;
  pageContext?: CopilotContextPayload | null;
  activePageIds?: string[];
  /** See `CopilotRequest.activeContexts`. */
  activeContexts?: CopilotContextPayload[];
  currentUrl?: string;
  /** See `CopilotRequest.clientTimezone` — a resumed turn rebuilds its prompt from this request. */
  clientTimezone?: string;
}

// ============================================================================
// Extended Callbacks (sub-agent delegation + page actions)
// ============================================================================

export interface CopilotStreamCallbacks extends BaseStreamCallbacks<CopilotResponse> {
  onSubAgentStart?: (subAgent: string) => void;
  onSubAgentThinking?: (subAgent: string, content: string) => void;
  onSubAgentThinkingDone?: (subAgent: string) => void;
  onSubAgentText?: (subAgent: string, content: string) => void;
  onSubAgentToolCall?: (subAgent: string, toolName: string, input: unknown) => void;
  onSubAgentToolResult?: (subAgent: string, toolName: string) => void;
  onSubAgentError?: (subAgent: string, message: string) => void;
  onSubAgentDone?: (subAgent: string) => void;
  onPageAction?: (type: string, data: Record<string, unknown>) => void;
}

// ============================================================================
// Copilot Service
// ============================================================================

@Injectable({
  providedIn: 'root',
})
export class CopilotService {
  constructor(private streamingChat: StreamingChatService) {}

  async resumeFromHitl(
    request: CopilotResumeRequest,
    callbacks: CopilotStreamCallbacks,
    signal?: AbortSignal,
  ): Promise<void> {
    const resumeRequest = { ...request, clientTimezone: request.clientTimezone ?? tz.guess() };

    await this.streamingChat.postAndStream('/api/copilot/resume', resumeRequest, callbacks, {
      signal,
      customEventHandler: (event, cb) => {
        const type = event.type as string;

        // Sub-agent lifecycle and progress events
        if (type.startsWith('sub-agent:')) {
          const subAgent = event.subAgent as string;

          switch (type) {
            case 'sub-agent:start':
              cb.onSubAgentStart?.(subAgent);
              break;
            case 'sub-agent:thinking':
              cb.onSubAgentThinking?.(subAgent, event.content as string);
              break;
            case 'sub-agent:thinking-done':
              cb.onSubAgentThinkingDone?.(subAgent);
              break;
            case 'sub-agent:text':
              cb.onSubAgentText?.(subAgent, event.content as string);
              break;
            case 'sub-agent:tool-call':
              cb.onSubAgentToolCall?.(subAgent, event.toolName as string, event.input);
              break;
            case 'sub-agent:tool-result':
              cb.onSubAgentToolResult?.(subAgent, event.toolName as string);
              break;
            case 'sub-agent:error':
              cb.onSubAgentError?.(subAgent, event.message as string);
              break;
            case 'sub-agent:done':
              cb.onSubAgentDone?.(subAgent);
              break;
          }

          return true;
        }

        // Page action events emitted by delegation tools. All FE-side actions
        // now flow through the unified `invoke-fe-tool` shape: the SSE event
        // carries `{ name, args }` and the panel dispatches via
        // `CopilotContextService.executeAction`.
        if (type === 'invoke-fe-tool') {
          cb.onPageAction?.(type, event as Record<string, unknown>);

          return true;
        }

        return false;
      },
    });
  }

  async sendMessageStreaming(
    request: CopilotRequest,
    callbacks: CopilotStreamCallbacks,
    signal?: AbortSignal,
  ): Promise<void> {
    const messageRequest = { ...request, clientTimezone: request.clientTimezone ?? tz.guess() };

    await this.streamingChat.postAndStream('/api/copilot', messageRequest, callbacks, {
      signal,
      customEventHandler: (event, cb) => {
        const type = event.type as string;

        // Sub-agent lifecycle and progress events
        if (type.startsWith('sub-agent:')) {
          const subAgent = event.subAgent as string;

          switch (type) {
            case 'sub-agent:start':
              cb.onSubAgentStart?.(subAgent);
              break;
            case 'sub-agent:thinking':
              cb.onSubAgentThinking?.(subAgent, event.content as string);
              break;
            case 'sub-agent:thinking-done':
              cb.onSubAgentThinkingDone?.(subAgent);
              break;
            case 'sub-agent:text':
              cb.onSubAgentText?.(subAgent, event.content as string);
              break;
            case 'sub-agent:tool-call':
              cb.onSubAgentToolCall?.(subAgent, event.toolName as string, event.input);
              break;
            case 'sub-agent:tool-result':
              cb.onSubAgentToolResult?.(subAgent, event.toolName as string);
              break;
            case 'sub-agent:error':
              cb.onSubAgentError?.(subAgent, event.message as string);
              break;
            case 'sub-agent:done':
              cb.onSubAgentDone?.(subAgent);
              break;
          }

          return true;
        }

        // Page action events emitted by delegation tools. All FE-side actions
        // now flow through the unified `invoke-fe-tool` shape: the SSE event
        // carries `{ name, args }` and the panel dispatches via
        // `CopilotContextService.executeAction`.
        if (type === 'invoke-fe-tool') {
          cb.onPageAction?.(type, event as Record<string, unknown>);

          return true;
        }

        return false;
      },
    });
  }
}
